import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import API from "../services/api";

const pretty = (v) => (v ? new Date(v).toLocaleString("en-IN") : "—");
const finiteNumberOr = (value, fallback) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const emptyMetaTemplate = () => ({ name: "", templateId: "" });

const normalizeMetaTemplates = (approvedTemplates, approvedTemplateIds = []) => {
  const rows = [];
  const seen = new Set();

  if (Array.isArray(approvedTemplates)) {
    approvedTemplates.forEach((item) => {
      const templateId = String(item?.templateId || item?.id || "").trim();
      const name = String(item?.name || item?.label || templateId || "").trim();
      if (!templateId || seen.has(templateId)) return;
      seen.add(templateId);
      rows.push({ name: name || templateId, templateId });
    });
  }

  if (Array.isArray(approvedTemplateIds)) {
    approvedTemplateIds.forEach((item) => {
      const templateId = String(item || "").trim();
      if (!templateId || seen.has(templateId)) return;
      seen.add(templateId);
      rows.push({ name: templateId, templateId });
    });
  }

  return rows.length ? rows : [emptyMetaTemplate()];
};

const defaultAutomationTemplates = () => ({
  registrationWelcome: { title: "Registration Welcome", templateId: "" },
  salaryApproved: { title: "Salary Approved", templateId: "" },
  paymentReleased: { title: "Payment Released", templateId: "" },
  vendorDispatch: { title: "Vendor Dispatch", templateId: "" },
  vendorPaymentReminder: { title: "Vendor Payment Reminder", templateId: "" },
  vendorRequirementApproved: { title: "Vendor Requirement Approved", templateId: "" },
  vendorRequirementRejected: { title: "Vendor Requirement Rejected", templateId: "" },
  vendorRequirementReminder: { title: "Vendor Requirement Reminder", templateId: "" },
  supplierDispatchReminder: { title: "Supplier Dispatch Reminder", templateId: "" },
});

const AUTOMATION_TEMPLATE_FIELDS = [
  { key: "registrationWelcome", label: "Registration Welcome", note: "Auto trigger on user registration and manual welcome send from admin lists" },
  { key: "salaryApproved", label: "Salary Approved", note: "Employee payroll approval auto trigger" },
  { key: "paymentReleased", label: "Payment Released", note: "Vendor/supplier payment release auto trigger" },
  { key: "vendorDispatch", label: "Vendor Dispatch", note: "Vendor dispatch update auto trigger" },
  { key: "vendorPaymentReminder", label: "Vendor Payment Reminder", note: "Outstanding vendor payment reminder auto/manual trigger" },
  { key: "vendorRequirementApproved", label: "Vendor Requirement Approved", note: "Vendor requirement approval auto trigger" },
  { key: "vendorRequirementRejected", label: "Vendor Requirement Rejected", note: "Vendor requirement rejection auto trigger" },
  { key: "vendorRequirementReminder", label: "Vendor Requirement Reminder", note: "Pending vendor requirement reminder" },
  { key: "supplierDispatchReminder", label: "Supplier Dispatch Reminder", note: "Pending supplier dispatch reminder" },
];

const AdminWhatsAppSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [runningPaymentReminders, setRunningPaymentReminders] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [cfg, setCfg] = useState({
    enabled: false,
    providerName: "",
    endpointUrl: "",
    httpMethod: "POST",
    timeoutMs: 15000,
    authType: "none",
    authHeaderName: "X-API-Key",
    authToken: "",
    hasAuthToken: false,
    senderId: "",
    journeyId: "",
    defaultTemplateId: "",
    deptId: "",
    callbackUrl: "",
    defaultCountryCode: "+91",
    requestHeadersText: "{}",
    requestBodyTemplateText: "",
    automationTemplates: defaultAutomationTemplates(),
    templates: {
      salary_notification: "",
      vendor_payment_reminder: "",
      vendor_requirement_reminder: "",
      supplier_dispatch_reminder: "",
      generic_test: "Hello {{name}}, this is WhatsApp test from Lotus Rice Mill Pvt. Ltd. portal.",
    },
    reminderGapHours: 24,
    paymentReminderDays: 7,
    lastTestAt: null,
    lastReminderRunAt: null,
  });
  const [metaTemplates, setMetaTemplates] = useState([emptyMetaTemplate()]);
  const [testForm, setTestForm] = useState({ to: "", name: "", templateName: "generic_test" });
  const [logs, setLogs] = useState([]);

  const metaTemplateOptions = useMemo(
    () => metaTemplates.map((item) => ({
      name: String(item?.name || "").trim(),
      templateId: String(item?.templateId || "").trim(),
    })).filter((item) => item.templateId),
    [metaTemplates]
  );

  const loadConfig = async () => {
    try {
      setLoading(true);
      const res = await API.get("/admin/whatsapp/config");
      const c = res.data?.config || {};
      const nextMetaTemplates = normalizeMetaTemplates(c.approvedTemplates, c.approvedTemplateIds);
      const fallbackDefaultTemplateId = String(c.defaultTemplateId || nextMetaTemplates[0]?.templateId || "").trim();
      setMetaTemplates(nextMetaTemplates);
      setCfg((prev) => ({
        ...prev,
        enabled: !!c.enabled,
        providerName: c.providerName || "",
        endpointUrl: c.endpointUrl || "",
        httpMethod: c.httpMethod || "POST",
        timeoutMs: c.timeoutMs || 15000,
        authType: c.authType || "none",
        authHeaderName: c.authHeaderName || "X-API-Key",
        authToken: "",
        hasAuthToken: !!c.hasAuthToken,
        senderId: c.senderId || "",
        journeyId: c.journeyId || "",
        defaultTemplateId: fallbackDefaultTemplateId,
        deptId: c.deptId || "",
        callbackUrl: c.callbackUrl || "",
        defaultCountryCode: c.defaultCountryCode || "+91",
        requestHeadersText: JSON.stringify(c.requestHeaders || {}, null, 2),
        requestBodyTemplateText: c.requestBodyTemplate ? JSON.stringify(c.requestBodyTemplate, null, 2) : "",
        automationTemplates: {
          ...defaultAutomationTemplates(),
          ...(c.automationTemplates || {}),
        },
        templates: {
          salary_notification: c.templates?.salary_notification || "",
          vendor_payment_reminder: c.templates?.vendor_payment_reminder || "",
          vendor_requirement_reminder: c.templates?.vendor_requirement_reminder || "",
          supplier_dispatch_reminder: c.templates?.supplier_dispatch_reminder || "",
          generic_test: c.templates?.generic_test || prev.templates.generic_test,
        },
        reminderGapHours: c.reminderGapHours || 24,
        paymentReminderDays: finiteNumberOr(c.paymentReminderDays, 7),
        lastTestAt: c.lastTestAt || null,
        lastReminderRunAt: c.lastReminderRunAt || null,
      }));
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load WhatsApp config");
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      setLogsLoading(true);
      const res = await API.get("/admin/whatsapp/logs?limit=50");
      setLogs(Array.isArray(res.data?.logs) ? res.data.logs : []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load logs");
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    loadLogs();
  }, []);

  const updateMetaTemplate = (index, field, value) => {
    setMetaTemplates((prev) => prev.map((item, idx) => (
      idx === index ? { ...item, [field]: value } : item
    )));
  };

  const addMetaTemplate = () => {
    setMetaTemplates((prev) => [...prev, emptyMetaTemplate()]);
  };

  const removeMetaTemplate = (index) => {
    setMetaTemplates((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      return next.length ? next : [emptyMetaTemplate()];
    });
  };

  const saveConfig = async () => {
    setMsg("");
    setErr("");
    try {
      setSaving(true);
      const approvedTemplates = metaTemplateOptions.map((item) => ({
        name: item.name || item.templateId,
        templateId: item.templateId,
      }));
      const defaultTemplateId = String(cfg.defaultTemplateId || "").trim() || approvedTemplates[0]?.templateId || "";
      const payload = {
        approvedTemplates,
        enabled: cfg.enabled,
        providerName: cfg.providerName,
        endpointUrl: cfg.endpointUrl,
        httpMethod: cfg.httpMethod,
        timeoutMs: Number(cfg.timeoutMs) || 15000,
        authType: cfg.authType,
        authHeaderName: cfg.authHeaderName,
        senderId: cfg.senderId,
        journeyId: cfg.journeyId,
        defaultTemplateId,
        approvedTemplateIds: approvedTemplates.map((item) => item.templateId),
        deptId: cfg.deptId,
        callbackUrl: cfg.callbackUrl,
        defaultCountryCode: cfg.defaultCountryCode,
        requestHeaders: cfg.requestHeadersText || "{}",
        requestBodyTemplate: cfg.requestBodyTemplateText || "",
        automationTemplates: cfg.automationTemplates,
        templates: cfg.templates,
        reminderGapHours: Number(cfg.reminderGapHours) || 24,
        paymentReminderDays: finiteNumberOr(cfg.paymentReminderDays, 7),
      };
      if (cfg.authToken?.trim()) payload.authToken = cfg.authToken.trim();
      const res = await API.post("/admin/whatsapp/config", payload);
      setMsg(res.data?.message || "Saved");
      await loadConfig();
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to save config");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setMsg("");
    setErr("");
    try {
      setTesting(true);
      const res = await API.post("/admin/whatsapp/test", testForm);
      setMsg(res.data?.message || "Test sent");
      await Promise.all([loadConfig(), loadLogs()]);
    } catch (e) {
      setErr(e?.response?.data?.message || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const runReminders = async () => {
    setMsg("");
    setErr("");
    try {
      setRunning(true);
      const res = await API.post("/admin/whatsapp/run-reminders");
      setMsg(res.data?.message || "Reminder job done");
      await Promise.all([loadConfig(), loadLogs()]);
    } catch (e) {
      setErr(e?.response?.data?.message || "Reminder run failed");
    } finally {
      setRunning(false);
    }
  };

  const runVendorPaymentReminders = async () => {
    setMsg("");
    setErr("");
    try {
      setRunningPaymentReminders(true);
      const res = await API.post("/admin/whatsapp/run-vendor-payment-reminders");
      setMsg(res.data?.message || "Vendor payment reminder job done");
      await Promise.all([loadConfig(), loadLogs()]);
    } catch (e) {
      setErr(e?.response?.data?.message || "Vendor payment reminder run failed");
    } finally {
      setRunningPaymentReminders(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h4 className="mb-3">WhatsApp Automation Settings</h4>
      {err && <Alert variant="danger">{err}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col md={2}>
              <Form.Check
                type="switch"
                label="Enable"
                checked={cfg.enabled}
                onChange={(e) => setCfg((p) => ({ ...p, enabled: e.target.checked }))}
              />
            </Col>
            <Col md={5}>
              <Form.Label>Provider Name</Form.Label>
              <Form.Control value={cfg.providerName} onChange={(e) => setCfg((p) => ({ ...p, providerName: e.target.value }))} />
            </Col>
            <Col md={5}>
              <Form.Label>Endpoint URL</Form.Label>
              <Form.Control value={cfg.endpointUrl} onChange={(e) => setCfg((p) => ({ ...p, endpointUrl: e.target.value }))} />
            </Col>

            <Col md={2}>
              <Form.Label>Method</Form.Label>
              <Form.Select value={cfg.httpMethod} onChange={(e) => setCfg((p) => ({ ...p, httpMethod: e.target.value }))}>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Timeout (ms)</Form.Label>
              <Form.Control type="number" value={cfg.timeoutMs} onChange={(e) => setCfg((p) => ({ ...p, timeoutMs: e.target.value }))} />
            </Col>
            <Col md={2}>
              <Form.Label>Auth Type</Form.Label>
              <Form.Select value={cfg.authType} onChange={(e) => setCfg((p) => ({ ...p, authType: e.target.value }))}>
                <option value="none">None</option>
                <option value="bearer">Bearer</option>
                <option value="api_key">API Key</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Auth Header</Form.Label>
              <Form.Control value={cfg.authHeaderName} onChange={(e) => setCfg((p) => ({ ...p, authHeaderName: e.target.value }))} />
            </Col>
            <Col md={3}>
              <Form.Label>Auth Token {cfg.hasAuthToken ? "(saved)" : ""}</Form.Label>
              <Form.Control
                type="password"
                placeholder={cfg.hasAuthToken ? "Leave blank to keep" : "Enter token"}
                value={cfg.authToken}
                onChange={(e) => setCfg((p) => ({ ...p, authToken: e.target.value }))}
              />
            </Col>

            <Col md={3}>
              <Form.Label>Sender ID</Form.Label>
              <Form.Control value={cfg.senderId} onChange={(e) => setCfg((p) => ({ ...p, senderId: e.target.value }))} />
            </Col>
            <Col md={3}>
              <Form.Label>Journey ID</Form.Label>
              <Form.Control value={cfg.journeyId} onChange={(e) => setCfg((p) => ({ ...p, journeyId: e.target.value }))} />
            </Col>
            <Col md={3}>
              <Form.Label>Default Meta Template</Form.Label>
              <Form.Select
                value={cfg.defaultTemplateId}
                onChange={(e) => setCfg((p) => ({ ...p, defaultTemplateId: e.target.value }))}
              >
                <option value="">Select default template</option>
                {metaTemplateOptions.map((item) => (
                  <option key={item.templateId} value={item.templateId}>
                    {(item.name || item.templateId)} ({item.templateId})
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Dept ID</Form.Label>
              <Form.Control value={cfg.deptId} onChange={(e) => setCfg((p) => ({ ...p, deptId: e.target.value }))} />
            </Col>
            <Col md={3}>
              <Form.Label>Callback URL</Form.Label>
              <Form.Control value={cfg.callbackUrl} onChange={(e) => setCfg((p) => ({ ...p, callbackUrl: e.target.value }))} />
            </Col>
            <Col md={3}>
              <Form.Label>Default Country Code</Form.Label>
              <Form.Control value={cfg.defaultCountryCode} onChange={(e) => setCfg((p) => ({ ...p, defaultCountryCode: e.target.value }))} />
            </Col>
            <Col md={3}>
              <Form.Label>Reminder Gap (hours)</Form.Label>
              <Form.Control type="number" value={cfg.reminderGapHours} onChange={(e) => setCfg((p) => ({ ...p, reminderGapHours: e.target.value }))} />
            </Col>
            <Col md={3}>
              <Form.Label>Payment Reminder Days</Form.Label>
              <Form.Control type="number" value={cfg.paymentReminderDays} onChange={(e) => setCfg((p) => ({ ...p, paymentReminderDays: e.target.value }))} />
              <div className="small text-muted mt-1">Outstanding payment itne din purani hote hi auto reminder bheja jayega.</div>
            </Col>
            <Col md={12}>
              <Form.Label>Automation Meta Templates</Form.Label>
              <div className="border rounded p-3 mb-2">
                {AUTOMATION_TEMPLATE_FIELDS.map((item) => (
                  <Row className="g-2 align-items-end mb-3" key={item.key}>
                    <Col md={4}>
                      <Form.Label>{item.label} Title</Form.Label>
                      <Form.Control
                        value={cfg.automationTemplates?.[item.key]?.title || ""}
                        onChange={(e) =>
                          setCfg((p) => ({
                            ...p,
                            automationTemplates: {
                              ...p.automationTemplates,
                              [item.key]: {
                                ...p.automationTemplates?.[item.key],
                                title: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                    </Col>
                    <Col md={5}>
                      <Form.Label>{item.label} Meta Template ID</Form.Label>
                      <Form.Control
                        value={cfg.automationTemplates?.[item.key]?.templateId || ""}
                        placeholder="Paste exact Meta template ID"
                        onChange={(e) =>
                          setCfg((p) => ({
                            ...p,
                            automationTemplates: {
                              ...p.automationTemplates,
                              [item.key]: {
                                ...p.automationTemplates?.[item.key],
                                templateId: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                    </Col>
                    <Col md={3}>
                      <div className="small text-muted pt-4">{item.note}</div>
                    </Col>
                  </Row>
                ))}
              </div>
              <div className="small text-muted">
                Jis automation ke liye yahan template ID save karoge, backend auto trigger me wahi Meta template use hoga.
              </div>
            </Col>
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Meta Templates</Form.Label>
                <Button size="sm" variant="outline-primary" onClick={addMetaTemplate}>Add Template</Button>
              </div>
              <div className="border rounded p-3 bg-light">
                {metaTemplates.map((item, index) => (
                  <Row className="g-2 align-items-end mb-2" key={`${item.templateId || "new"}-${index}`}>
                    <Col md={5}>
                      <Form.Label>Template Name</Form.Label>
                      <Form.Control
                        value={item.name}
                        placeholder="Welcome Message"
                        onChange={(e) => updateMetaTemplate(index, "name", e.target.value)}
                      />
                    </Col>
                    <Col md={5}>
                      <Form.Label>Meta Template ID</Form.Label>
                      <Form.Control
                        value={item.templateId}
                        placeholder="01kja2jbkf4kwvfecyxj424ekv"
                        onChange={(e) => updateMetaTemplate(index, "templateId", e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Button
                        variant="outline-danger"
                        className="w-100"
                        onClick={() => removeMetaTemplate(index)}
                        disabled={metaTemplates.length === 1 && !item.name && !item.templateId}
                      >
                        Remove
                      </Button>
                    </Col>
                  </Row>
                ))}
              </div>
              <div className="small text-muted mt-2">
                Yahin saved Meta template name aur ID project ke sab WhatsApp selectors me dikhenge, aur selected item ka same template ID send API ko jayega.
              </div>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Label>Extra Headers JSON</Form.Label>
              <Form.Control as="textarea" rows={5} value={cfg.requestHeadersText} onChange={(e) => setCfg((p) => ({ ...p, requestHeadersText: e.target.value }))} />
            </Col>
            <Col md={6}>
              <Form.Label>Request Body Template JSON (optional)</Form.Label>
              <Form.Control as="textarea" rows={5} value={cfg.requestBodyTemplateText} onChange={(e) => setCfg((p) => ({ ...p, requestBodyTemplateText: e.target.value }))} />
              <div className="small text-muted mt-1">Use placeholders like {"{{to}}"}, {"{{message}}"}, {"{{senderId}}"}</div>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Label>Salary Template Key / Meta Template ID</Form.Label>
              <Form.Control as="textarea" rows={3} value={cfg.templates.salary_notification} onChange={(e) => setCfg((p) => ({ ...p, templates: { ...p.templates, salary_notification: e.target.value } }))} />
            </Col>
            <Col md={6}>
              <Form.Label>Vendor Reminder Template Key / Meta Template ID</Form.Label>
              <Form.Control as="textarea" rows={3} value={cfg.templates.vendor_requirement_reminder} onChange={(e) => setCfg((p) => ({ ...p, templates: { ...p.templates, vendor_requirement_reminder: e.target.value } }))} />
            </Col>
            <Col md={6}>
              <Form.Label>Vendor Payment Reminder Template Key / Meta Template ID</Form.Label>
              <Form.Control as="textarea" rows={3} value={cfg.templates.vendor_payment_reminder} onChange={(e) => setCfg((p) => ({ ...p, templates: { ...p.templates, vendor_payment_reminder: e.target.value } }))} />
            </Col>
            <Col md={6}>
              <Form.Label>Supplier Reminder Template Key / Meta Template ID</Form.Label>
              <Form.Control as="textarea" rows={3} value={cfg.templates.supplier_dispatch_reminder} onChange={(e) => setCfg((p) => ({ ...p, templates: { ...p.templates, supplier_dispatch_reminder: e.target.value } }))} />
            </Col>
            <Col md={6}>
              <Form.Label>Generic Test Template</Form.Label>
              <Form.Control as="textarea" rows={3} value={cfg.templates.generic_test} onChange={(e) => setCfg((p) => ({ ...p, templates: { ...p.templates, generic_test: e.target.value } }))} />
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-3">
            <Button onClick={saveConfig} disabled={saving}>{saving ? "Saving..." : "Save Settings"}</Button>
            <Button variant="outline-secondary" onClick={loadConfig}>Reload</Button>
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <h6>Test & Run</h6>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label>Test Number</Form.Label>
              <Form.Control value={testForm.to} onChange={(e) => setTestForm((p) => ({ ...p, to: e.target.value }))} placeholder="91xxxxxxxxxx" />
            </Col>
            <Col md={3}>
              <Form.Label>Name</Form.Label>
              <Form.Control value={testForm.name} onChange={(e) => setTestForm((p) => ({ ...p, name: e.target.value }))} />
            </Col>
            <Col md={3}>
              <Form.Label>Template</Form.Label>
              <Form.Select value={testForm.templateName} onChange={(e) => setTestForm((p) => ({ ...p, templateName: e.target.value }))}>
                <option value="generic_test">generic_test</option>
                <option value="salary_notification">salary_notification</option>
                <option value="vendor_requirement_reminder">vendor_requirement_reminder</option>
                <option value="supplier_dispatch_reminder">supplier_dispatch_reminder</option>
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex gap-2">
              <Button variant="outline-primary" onClick={sendTest} disabled={testing}>{testing ? "Sending..." : "Send Test"}</Button>
              <Button variant="outline-success" onClick={runReminders} disabled={running}>{running ? "Running..." : "Run Reminders Now"}</Button>
            </Col>
            <Col md={12}>
              <div className="d-flex gap-2 align-items-center mt-2">
                <Button variant="outline-warning" onClick={runVendorPaymentReminders} disabled={runningPaymentReminders}>
                  {runningPaymentReminders ? "Sending..." : "Send Vendor Payment Reminders"}
                </Button>
                <div className="small text-muted">
                  Manual run sab outstanding vendors ko turant reminder bhejega. Auto run `Payment Reminder Days` ke hisaab se hota hai.
                </div>
              </div>
            </Col>
          </Row>
          <div className="small text-muted mt-2">Last test: {pretty(cfg.lastTestAt)} • Last reminder run: {pretty(cfg.lastReminderRunAt)}</div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Recent WhatsApp Logs</h6>
            <Button size="sm" variant="outline-secondary" onClick={loadLogs} disabled={logsLoading}>{logsLoading ? "Loading..." : "Refresh Logs"}</Button>
          </div>
          <div className="table-responsive">
            <Table size="sm" hover>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>To</th>
                  <th>Template</th>
                  <th>Status</th>
                  <th>Module</th>
                  <th>Message / Error</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-muted py-3">No logs found</td></tr>
                ) : logs.map((log) => (
                  <tr key={log._id}>
                    <td>{pretty(log.createdAt)}</td>
                    <td>{log.to || "—"}</td>
                    <td>{log.templateName || "—"}</td>
                    <td>{log.status || "—"}</td>
                    <td>{log.sourceModule || "—"}</td>
                    <td style={{ maxWidth: 360, whiteSpace: "pre-wrap" }}>{log.error || log.message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminWhatsAppSettings;
